import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import RectangularOffersSlider from "../components/RectangularOffersSlider";
import SquareOffersSlider from "../components/SquareOffersSlider";
import SquareOffersSlider2 from "../components/SquareOffersSlider2";
import CircularOffersSlider from "../components/CircularOffersSlider";
import FeaturesIconsSlider from "../components/FeaturesIconsSlider";
import CircularCategoriesSlider from "../components/CircularCategoriesSlider";
import MobileCategoriesGrid from "../components/MobileCategoriesGrid";
import CarouselSlider from "../components/CarouselSlider";
import MobileSlidesSlider from "../components/MobileSlidesSlider";
import SquareCategoriesSlider from "../components/SquareCategoriesSlider";
import NewsTicker from "../components/NewsTicker";
import GoTo from "../components/GoTo";
import { useCategories } from "../utils/useCategories";
import { useOffers } from "../utils/useOffers";
import { createSlug } from "../utils/slug";
import OffersTabs from "../components/OffersTabs";
import AuthModal from "../components/AuthModal";
import MasonryLayoutV4 from "../components/MasonryLayoutV4";
import BannerCollection from "../components/BannerCollection";
import BannerCollectionSlider from "../components/BannerCollectionSlider";
import TextImageLayout from "../components/TextImageLayout";
import Lookbook from "../components/Lookbook";
import BannerHeroCollection from "../components/BannerHeroCollection";
import MasonryLayoutStyle2 from "../components/MasonryLayoutStyle2";
import BrandCarousel from "../components/BrandCarousel";
import BannerWithProducts from "../components/BannerWithProducts";
import MasonryLayoutV2 from "../components/MasonryLayoutV2";
import ProductsSlider from "../components/ProductsSlider";
import { useLanguage } from "../context/LanguageContext";
import { usePageMeta } from "../utils/usePageMeta";

function HomePage() {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authView, setAuthView] = useState("login");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768,
  );
  const { language } = useLanguage();
  
  // Set page meta tags
  usePageMeta({
    title: language === "ar" 
      ? "عبدالعزيز السلاب - الحلول المثالية للمنزل والحمام"
      : "Abdelaziz Elsallab - Premium Home & Bathroom Solutions",
    description: language === "ar"
      ? "تسوق من عبدالعزيز السلاب - أفضل العروض على البلاط، الأدوات الصحية، الخلاطات، واكسسوارات الحمام. اكتشف مجموعاتنا الحصرية والعروض الخاصة."
      : "Shop from Abdelaziz Elsallab - Your trusted source for premium tiles, sanitary ware, mixers, and bathroom accessories. Discover our exclusive collections and special offers.",
  });
  
  const { categories, isLoading, isError } = useCategories();
  const { addItem, open } = useCart();
  const {
    offers,
    bannerOffers,
    isLoading: isLoadingOffers,
    isError: isErrorOffers,
    spotlightOffersGrouped,
  } = useOffers();

  // Helper to get offer link by ID (for hardcoded offer IDs)
  const getOfferLink = (offerId) => {
    if (!offerId || !offers) return `/shop/default?offer_id=${offerId}`;
    const offer = offers.find((o) => String(o.id) === String(offerId));
    if (offer && offer.name) {
      // Include both slug (for SEO) and offer_id (for immediate API call)
      return `/shop/default?offer=${encodeURIComponent(createSlug(offer.name))}&offer_id=${offerId}`;
    }
    return `/shop/default?offer_id=${offerId}`;
  };

  // Fetch banner offers separately - don't wait for regular offers
  const {
    data: bannerOffersData,
    isLoading: isLoadingBannerOffers,
    isError: isErrorBannerOffers,
  } = useQuery({
    queryKey: ["bannerOffers"],
    queryFn: async () => {
      const url =
        "https://erp-elsalab.odoo.com/api/cs/banner-offers?include_website_only=1";
      const res = await fetch(url, {
        headers: { Accept: "application/json; charset=utf-8" },
      });
      if (!res.ok) throw new Error("Failed to load banner offers");
      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      const data = JSON.parse(text);
      return data?.data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  });

  // Track mobile/desktop screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Wait for DOM to be ready and Swiper to be available
    const initializeSliders = () => {
      if (!window.Swiper) {
        console.log("Swiper not available yet, retrying...");
        setTimeout(initializeSliders, 100);
        return;
      }
    };

    // Initial attempt
    initializeSliders();

    // Retry after a delay to ensure DOM is fully loaded
    const timer = setTimeout(initializeSliders, 1000);

    // Additional retry for mobile sliders
    const mobileTimer = setTimeout(initializeSliders, 2000);

    return () => {
      clearTimeout(timer);
      clearTimeout(mobileTimer);
    };
  }, []);

  // Preload all mobile slider images while preloader is visible
  useEffect(() => {
    if (isLoadingOffers || isLoading) {
      // Determine which slides will be used
      const dynamicSlides = (bannerOffers || []).map((o) => ({
        image: o.imageUrl,
        alt: o.name,
        title: "",
        description: o.promotionalText || "",
        buttonText: language === "ar" ? "تسوق المجموعة" : "Shop collection",
        buttonLink:
          o.id && o.name
            ? `/shop/default?offer=${encodeURIComponent(createSlug(o.name))}&offer_id=${o.id}`
            : o.id
              ? `/shop/default?offer_id=${o.id}`
              : "/shop-default",
      }));
      const fallbackSlides = [
        {
          image: "/assets/images/slider/667799.png",
          alt: "667799",
          buttonText: language === "ar" ? "تسوق المجموعة" : "Shop collection",
          buttonLink: "/shop/collection",
        },
        {
          image: "/assets/images/slider/667789.png",
          alt: "667789",
          buttonText: language === "ar" ? "تسوق المجموعة" : "Shop collection",
          buttonLink: "/shop/collection",
        },
        {
          image: "/assets/images/slider/667788.png",
          alt: "667788",
          buttonText: language === "ar" ? "تسوق المجموعة" : "Shop collection",
          buttonLink: "/shop/collection",
        },
      ];
      const slides =
        dynamicSlides.length > 0 && !isErrorOffers
          ? dynamicSlides
          : fallbackSlides;

      // Preload all slider images aggressively
      slides.forEach((slide, index) => {
        if (slide.image) {
          const img = new Image();
          // Set high priority for first 2 images
          if (index < 2) {
            img.fetchPriority = "high";
          }
          img.src = slide.image;
        }
      });
    }
  }, [bannerOffers, isLoadingOffers, isLoading, isErrorOffers, language]);

  return (
    <div className="preload-wrapper popup-loader page-home">
      <style>
        {`
          /* Top carousel (hero) height */
          @media (min-width: 768px) {
          .tf-slideshow,
          .tf-slideshow .swiper,
          .tf-slideshow .swiper-wrapper,
          .tf-slideshow .swiper-slide,
          .tf-slideshow .wrap-slider {
              height: 55vh !important; /* reduced height */
              max-height: 650px !important;
              min-height: 400px !important;
            }
            .tf-slideshow .swiper-wrapper {
              display: flex !important;
              align-items: stretch !important;
            }
            .tf-slideshow .swiper-slide {
              display: flex !important;
              flex-direction: column !important;
              height: 100% !important;
              align-items: stretch !important;
          }
          .tf-slideshow .wrap-slider {
              overflow: hidden !important;
              height: 100% !important;
              display: block !important;
              position: relative !important;
              margin: 0 !important;
              padding: 0 !important;
          }
            .tf-slideshow .wrap-slider > a {
              height: 100% !important;
              width: 100% !important;
              display: block !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .tf-slideshow .wrap-slider img,
            .tf-slideshow .wrap-slider img[style],
            .tf-slideshow .wrap-slider a img {
              width: 100% !important;
              height: 100% !important;
              max-width: 100% !important;
              max-height: 100% !important;
              object-fit: cover !important;
              object-position: center !important;
              display: block !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .tf-slideshow .wrap-slider .box-content {
              display: none !important;
              height: 0 !important;
              width: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden !important;
            }
          }

          /* Hide carousel slider on mobile */
          .desktop-carousel-wrapper {
            display: block;
          }

          /* Hide desktop categories slider on mobile */
          .desktop-categories-wrapper {
            display: block;
          }

          @media (max-width: 767px) {
            .desktop-carousel-wrapper {
              display: none !important;
            }
            .desktop-categories-wrapper {
              display: none !important;
            }
            .banner-collection-wrapper {
              display: none !important;
            }

            /* Add separator after each offers slider on mobile */
            .home-offer-block {
              position: relative;
              margin-bottom: 0;
              padding-bottom: 16px;
            }

            .home-offer-block::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              width: 100%;
              height: 16px;
              background-color: #FAFAFA;
            }

            /* Add separator after ProductsSlider on mobile */
            .products-slider-section {
              position: relative;
              margin-bottom: 0;
              margin-top: 0 !important;
              padding-bottom: 16px;
              padding-top: 0 !important;
            }

            .products-slider-section::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              width: 100%;
              height: 16px;
              background-color: #FAFAFA;
            }
          }
          @media (max-width: 991px) {
            .tf-slideshow,
            .tf-slideshow .swiper,
            .tf-slideshow .swiper-wrapper,
            .tf-slideshow .swiper-slide,
            .tf-slideshow .wrap-slider {
              height: 35vh !important;
              max-height: 450px !important;
              min-height: 300px !important;
            }
          }
          @media (max-width: 575px) {
            .tf-slideshow,
            .tf-slideshow .swiper,
            .tf-slideshow .swiper-wrapper,
            .tf-slideshow .swiper-slide,
            .tf-slideshow .wrap-slider {
              height: 30vh !important;
              max-height: 400px !important;
              min-height: 250px !important;
            }
          }

          /* Reduce space above carousel slider - significantly */
          #circularCategoriesSlider-section {
            margin-bottom: -30px !important;
            padding-bottom: 0 !important;
          }

          /* Remove spacing from categories wrapper */
          @media (min-width: 768px) {
            .desktop-categories-wrapper {
              margin-bottom: -30px !important;
              padding-bottom: 0 !important;
            }
          }

          /* Remove spacing from carousel slider wrapper */
          @media (min-width: 768px) {
            .desktop-carousel-wrapper {
              margin-top: -30px !important;
              padding-top: 0 !important;
            }
          }

          /* Apply Tajawal font to top categories */
          #circularCategoriesSlider-section .collection-item-circle .collection-content .title {
            font-family: "Tajawal", "Noto Sans Arabic", "Omnes Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif !important;
            font-size: 12px !important;
          }
          
          .carousel-slider-wrapper {
            margin-top: -20px !important;
            padding-top: 0 !important;
            /* Banner width constraints are handled in CarouselSlider component */
          }

          /* Reduce carousel slider width to match container width (1540px) */
          @media (min-width: 768px) {
            .page-home .desktop-carousel-wrapper {
              max-width: 1540px !important;
              margin-left: auto !important;
              margin-right: auto !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }

            .page-home .desktop-carousel-wrapper .carousel-slider-wrapper {
              max-width: 100% !important;
              width: 100% !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }

            .page-home .desktop-carousel-wrapper .carousel-slider-wrapper .tf-slideshow {
              max-width: 100% !important;
              width: 100% !important;
              height: auto !important;
              min-height: 300px !important;
            }
            
            .page-home .desktop-carousel-wrapper .carousel-slider-wrapper .tf-slideshow .wrap-slider {
              height: auto !important;
              min-height: 300px !important;
            }
            
            .page-home .desktop-carousel-wrapper .carousel-slider-wrapper .tf-slideshow .wrap-slider img {
              height: auto !important;
              min-height: 300px !important;
            }

            .page-home .desktop-carousel-wrapper .carousel-slider-wrapper .tf-slideshow .swiper {
              height: 100% !important;
            }

            /* Tablet size - reduce padding */
            @media (min-width: 768px) and (max-width: 1199px) {
              .page-home .desktop-carousel-wrapper {
                padding-left: 25px !important;
                padding-right: 25px !important;
              }
            }

            /* Override CarouselSlider media queries */
            @media (min-width: 1400px) {
              .page-home .desktop-carousel-wrapper {
                padding-left: 50px !important;
                padding-right: 50px !important;
              }
              .page-home .desktop-carousel-wrapper .carousel-slider-wrapper {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
            }

            @media (min-width: 1600px) {
              .page-home .desktop-carousel-wrapper {
                padding-left: 60px !important;
                padding-right: 60px !important;
              }
              .page-home .desktop-carousel-wrapper .carousel-slider-wrapper {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
            }
          }

          /* Distinctive styling for offer slider section titles - matching ShopCollection filter buttons */
          .flat-title .title,
          .flat-title-v2 .title,
          .flat-title .title.wow,
          .flat-title-v2 .title.wow {
            position: relative;
            display: inline-block;
            padding: 10px 16px 10px 14px;
            border-radius: 10px;
            // background: linear-gradient(135deg, rgba(219,18,21,0.12), rgba(0,0,0,0.02));
            backdrop-filter: saturate(1.2);
            font-family: "Noto Sans Arabic", "Omnes Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif !important;
            font-weight: 600 !important;
            letter-spacing: 0.5px !important;
            text-transform: uppercase;
            font-feature-settings: 'kern' 1, 'liga' 1, 'calt' 1;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          // .flat-title .title::before,
          // .flat-title-v2 .title::before {
          //   content: "";
          //   position: absolute;
          //   left: 0;
          //   top: 0;
          //   bottom: 0;
          //   width: 4px;
          //   border-radius: 10px 0 0 10px;
          //   background: var(--primary);
          // }
          .flat-title .title,
          .flat-title-v2 .title { color: var(--main); }
          .flat-title .title strong,
          .flat-title-v2 .title strong { color: var(--primary); }
          /* Red border around section titles */
          .flat-title .title,
          .flat-title-v2 .title {
            border: 2px solid var(--primary);
            border-radius: 8px;     /* تقدر تزودها/تقللها حسب ذوقك */
            padding: 6px 16px;       /* عشان النص مايلزقش في الإطار */
            background: #fff;        /* اختياري: يبرز الإطار لو الخلفية غامقة */
          }
            
/* درجات لبني أخف */
:root{
  --hover-blue-md: #3b82f6;   /* لبني متوسط */
  --hover-blue-lt: #93c5fd;   /* لبني فاتح */
}

/* تثبيت شكل العنوان ومنع أي shift */
.flat-title .title,
.flat-title-v2 .title{
  position: relative;
  z-index: 1;
  background: transparent;                 /* سيب الخلفية شفافة */
  color: var(--main);                      /* خليه لون النص المعتاد */
  transition: color .2s ease;              /* مفيش تغيير مقاسات */
  overflow: hidden;                        /* نخبي طبقة الباكجراوند جوه */
}

/* طبقة الباكجراوند المتحركة (شفافة عشان النص يظهر) */
.flat-title .title::after,
.flat-title-v2 .title::after{
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(59,130,246,0.28) 0%,   /* لبني متوسط شفاف */
    rgba(147,197,253,0.28) 100% /* لبني فاتح شفاف */
  );
  transform: translateX(-100%);            /* مخفية ناحية الشمال */
  transition: transform .4s ease, opacity .2s ease;
  z-index: 0;                               /* خلف النص */
  pointer-events: none;                     /* مايعطلش الكِلك */
}

/* عند الهوفر: تنزلق وتزود شفافية بسيط */
.flat-title .title:hover::after,
.flat-title-v2 .title:hover::after{
  transform: translateX(0);
  opacity: 1;
}

/* لو عندك الشريط الجانبي ::before وعايزه يبان فوق الخلفية */
.flat-title .title::before,
.flat-title-v2 .title::before{
  position: relative;
  z-index: 2;        /* الشريط يفضل فوق طبقة ::after ومش يغطي النص */
}

/* احترام تفضيل تقليل الحركة */
@media (prefers-reduced-motion: reduce){
  .flat-title .title::after,
  .flat-title-v2 .title::after{
    transition: none;
    transform: none; /* تبقى ثابتة */
  }
}


          /* Unified vertical spacing between offer sliders - Reduced */
          .home-offer-block { 
            margin: 5px 0; 
          }
          @media (max-width: 991px) { 
            .home-offer-block { 
              margin: 4px 0; 
            } 
          }
          @media (max-width: 575px) { 
            .home-offer-block { 
              margin: 3px 0; 
            } 
          }

          /* Reduce spacing between offer slider and products section - but not for special-offers-products-section */
          .home-offer-block + section.flat-spacing-2:not(.special-offers-products-section),
          section.flat-spacing-2[style*="marginTop"]:not(.special-offers-products-section) {
            margin-top: -40px !important;
            padding-top: 0px !important;
          }
          
          /* Reduce padding for products sections that come after offer blocks - but not for special-offers-products-section */
          .home-offer-block ~ section.flat-spacing-2:not(.special-offers-products-section) {
            padding-top: 0px !important;
            margin-top: -40px !important;
          }
          
          /* General flat-spacing-2 reduction */
          .flat-spacing-2 {
            padding-top: 0px !important;
            padding-bottom: 15px !important;
          }
          
          /* Reduce bottom spacing from offer slider components */
          .home-offer-block .flat-spacing-27-edit {
            margin-bottom: 0px !important;
            padding-bottom: 0px !important;
          }
          
          /* Reduce top padding from offer sliders */
          .home-offer-block section[class*="flat-spacing"] {
            padding-top: 5px !important;
            padding-bottom: 0px !important;
            margin-bottom: 0px !important;
          }
          
          /* Specifically target SquareOffersSlider large padding */
          .home-offer-block section[style*="paddingTop"] {
            padding-top: 5px !important;
          }
          
          /* Target all sections inside home-offer-block */
          .home-offer-block > section {
            margin-bottom: 0px !important;
            padding-bottom: 0px !important;
          }
          
          /* Remove any gap between offer block and products section */
          .home-offer-block {
            margin-bottom: 0px !important;
            padding-bottom: 0px !important;
          }
          
          /* Remove container padding that adds space */
          .home-offer-block ~ section.flat-spacing-2 .container {
            padding-top: 0px !important;
            margin-top: 0px !important;
          }
          
          /* Negative margin to pull products section closer - but not for special-offers-products-section */
          section.flat-spacing-2[style*="marginTop"]:not(.special-offers-products-section) {
            margin-top: -60px !important;
          }

          /* Increase spacing after BannerCollection */
          .banner-collection-wrapper {
            margin-bottom: 60px !important;
            padding-bottom: 0 !important;
          }
          .banner-collection-wrapper section.flat-spacing-10 {
            margin-bottom: 0 !important;
          }

          /* Add small spacing above Special Offers section - override all conflicting rules */
          section.special-offers-products-section,
          .home-offer-block ~ section.special-offers-products-section,
          .home-offer-block + section.special-offers-products-section,
          section.flat-spacing-2.special-offers-products-section,
          section.flat-spacing-2.special-offers-products-section[style*="marginTop"] {
            margin-top: 10px !important;
            padding-top: 5px !important;
          }
          
          section.special-offers-products-section .container {
            padding-top: 5px !important;
            margin-top: 0px !important;
          }
          
          /* Reduce spacing below Special Offers title */
          section.flat-spacing-2 .container .flat-title.flex-row.justify-content-center {
            margin-bottom: 5px !important;
          }
          
          /* Add spacing for products slider section */
          .products-slider-section section.special-offers-products-section {
            margin-top: 10px !important;
            padding-top: 5px !important;
          }
          
          .products-slider-section section.special-offers-products-section .container {
            padding-top: 5px !important;
            margin-top: 0px !important;
          }

          /* Products section styling to match ShopDefault.jsx */
          @media only screen and (max-width: 575px) {
            /* Ensure wishlist button shows on mobile within product cards */
            .card-product .list-product-btn .wishlist {
              display: inline-flex !important;
              visibility: visible !important;
              opacity: 1 !important;
            }

            /* Make prices same size as desktop view on mobile */
            .card-product .price-area .current-price,
            .card-product .price-area .price.current-price,
            .card-product .card-product-info .price-area .current-price,
            .card-product .card-product-info .price-area .price.current-price,
            .card-product .card-product-info .price-area span.price.current-price {
              font-size: 1rem !important;
              line-height: 1rem !important;
            }
            
            .card-product .price-area .compare-at-price,
            .card-product .price-area .price.compare-at-price,
            .card-product .card-product-info .price-area .compare-at-price,
            .card-product .card-product-info .price-area .price.compare-at-price,
            .card-product .card-product-info .price-area span.price.compare-at-price {
              font-size: 0.9rem !important;
              line-height: 0.9rem !important;
            }
          }

          /* Make cart (bag) and favorite (heart) icons red within product cards */
          .card-product .list-product-btn .icon,
          .card-product .list-product-btn .icon:before {
            color: var(--primary) !important;
          }
          
          /* Hover effect: only change icon color, not background */
          .card-product .list-product-btn .box-icon {
            background-color: rgba(26, 115, 232, 0.15) !important;
            transition: background-color 0.3s ease, color 0.3s ease !important;
          }
          
          .card-product .list-product-btn .box-icon:hover {
            background-color: rgba(26, 115, 232, 0.15) !important; /* Keep same background */
            color: #0e5aa7 !important; /* Only change icon color to blue */
          }
          
          .card-product .list-product-btn .box-icon:hover .icon,
          .card-product .list-product-btn .box-icon:hover .icon:before {
            color: #0e5aa7 !important; /* Change icon color on hover */
          }

          /* Add separation and elevation to product items */
          .card-product.grid,
          .card-product.list-layout {
            background: #fafafa; /* off-white */
            border: 1px solid rgba(14, 90, 167, 0.5);
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(14, 90, 167, 0.15);
            padding: 12px;
          }

          /* Product image framing with bluish border */
          .card-product .product-img {
            display: block;
            border-radius: 12px;
            background: #ffffff;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
            outline: 1px solid rgba(14, 90, 167, 0.4);
            transition: box-shadow 0.25s ease, transform 0.25s ease;
            position: relative;
          }

          .card-product .product-img img {
            border-radius: 10px;
          }

          .card-product:hover .product-img {
            transform: translateY(-2px);
            box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
          }

          /* Ensure inner content respects rounded corners */
          .card-product .card-product-wrapper,
          .card-product .card-product-info {
            border-radius: 10px;
            overflow: hidden;
          }

          /* Smaller price text */
          .card-product .price-area .current-price {
            font-weight: 600;
            font-size: 1.1rem;
          }
          .card-product .price-area .compare-at-price {
            font-weight: 400;
            font-size: 1rem;
            opacity: 0.85;
            position: relative;
            text-decoration: none !important;
          }
          
          .card-product .price-area .compare-at-price::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            width: 100%;
            height: 1px;
            background-color: #000;
            transform: rotate(-15deg);
            transform-origin: center;
          }

          /* Cool Random Image Section Styling */
          .cool-random-image-section {
            margin: 8px 0;
            padding: 4px 0;
            background: linear-gradient(135deg, rgba(219, 18, 21, 0.03), rgba(0, 0, 0, 0.02));
            position: relative;
            overflow: hidden;
          }

          .cool-random-image-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: 
              radial-gradient(circle at 20% 20%, rgba(219, 18, 21, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(0, 0, 0, 0.05) 0%, transparent 50%);
            pointer-events: none;
          }

          .cool-image-wrapper {
            position: relative;
            max-width: 1200px;
            margin: 0 auto;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 
              0 20px 60px rgba(0, 0, 0, 0.15),
              0 8px 25px rgba(219, 18, 21, 0.1);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            display: block;
            text-decoration: none;
            color: inherit;
          }

          .cool-image-wrapper:hover {
            transform: translateY(-8px);
            box-shadow: 
              0 30px 80px rgba(0, 0, 0, 0.2),
              0 12px 35px rgba(219, 18, 21, 0.15);
          }

          .cool-image-container {
            position: relative;
            width: 100%;
            height: 400px;
            overflow: hidden;
          }

          .cool-random-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            transition: all 0.6s ease;
            filter: brightness(0.9) contrast(1.1);
          }

          .cool-image-wrapper:hover .cool-random-image {
            transform: scale(1.05);
            filter: brightness(1) contrast(1.2);
          }

          .cool-image-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(
              135deg,
              rgba(0, 0, 0, 0.4) 0%,
              rgba(0, 0, 0, 0.2) 50%,
              rgba(219, 18, 21, 0.3) 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: all 0.4s ease;
          }

          .cool-image-wrapper:hover .cool-image-overlay {
            opacity: 1;
          }

          .cool-image-content {
            text-align: center;
            color: white;
            transform: translateY(20px);
            transition: all 0.4s ease;
          }

          .cool-image-wrapper:hover .cool-image-content {
            transform: translateY(0);
          }

          .cool-image-title {
            font-size: 2.5rem;
            font-weight: 800;
            margin-bottom: 12px;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
            letter-spacing: 1px;
            font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }

          .cool-image-description {
            font-size: 1.1rem;
            margin-bottom: 24px;
            opacity: 0.9;
            text-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
            font-weight: 400;
          }

          .cool-image-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.95);
            color: var(--primary);
            padding: 14px 28px;
            border-radius: 50px;
            font-weight: 700;
            font-size: 1rem;
            letter-spacing: 0.5px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
            backdrop-filter: blur(10px);
            cursor: pointer;
          }

          .cool-image-wrapper:hover .cool-image-btn {
            background: white;
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            color: var(--primary);
          }

          .cool-image-btn i {
            font-size: 0.9rem;
            transition: transform 0.3s ease;
          }

          .cool-image-btn:hover i {
            transform: translateX(4px);
          }

          .cool-image-decoration {
            position: absolute;
            top: 20px;
            right: 20px;
            width: 60px;
            height: 60px;
            border: 3px solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            opacity: 0;
            transition: all 0.4s ease;
          }

          .cool-image-wrapper:hover .cool-image-decoration {
            opacity: 1;
            transform: scale(1.2);
          }

          .cool-image-decoration::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 20px;
            height: 20px;
            background: rgba(255, 255, 255, 0.6);
            border-radius: 50%;
          }

          /* Animation Classes for Different Effects */
          .animation-slide-up .cool-image-content {
            animation: slideUpIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-fade-in .cool-image-content {
            animation: fadeInScale 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-scale-in .cool-image-content {
            animation: scaleInBounce 0.9s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }

          .animation-slide-left .cool-image-content {
            animation: slideLeftIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-bounce-in .cool-image-content {
            animation: bounceIn 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }

          .animation-rotate-in .cool-image-content {
            animation: rotateIn 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-zoom-in .cool-image-content {
            animation: zoomIn 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-slide-right .cool-image-content {
            animation: slideRightIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-flip-in .cool-image-content {
            animation: flipIn 1.1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animation-glow-in .cool-image-content {
            animation: glowIn 1.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          /* Section-level animations */
          .animation-slide-up {
            animation: slideUpSection 0.6s ease-out;
          }

          .animation-fade-in {
            animation: fadeInSection 0.8s ease-out;
          }

          .animation-scale-in {
            animation: scaleInSection 0.7s ease-out;
          }

          .animation-slide-left {
            animation: slideLeftSection 0.6s ease-out;
          }

          .animation-bounce-in {
            animation: bounceInSection 0.9s ease-out;
          }

          .animation-rotate-in {
            animation: rotateInSection 0.8s ease-out;
          }

          .animation-zoom-in {
            animation: zoomInSection 0.7s ease-out;
          }

          .animation-slide-right {
            animation: slideRightSection 0.6s ease-out;
          }

          .animation-flip-in {
            animation: flipInSection 0.8s ease-out;
          }

          .animation-glow-in {
            animation: glowInSection 0.9s ease-out;
          }

          /* Keyframe Animations */
          @keyframes slideUpIn {
            0% {
              opacity: 0;
              transform: translateY(40px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeInScale {
            0% {
              opacity: 0;
              transform: scale(0.8);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes scaleInBounce {
            0% {
              opacity: 0;
              transform: scale(0.3);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.1);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes slideLeftIn {
            0% {
              opacity: 0;
              transform: translateX(-40px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes bounceIn {
            0% {
              opacity: 0;
              transform: translateY(-30px) scale(0.8);
            }
            50% {
              opacity: 0.8;
              transform: translateY(10px) scale(1.05);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes rotateIn {
            0% {
              opacity: 0;
              transform: rotate(-10deg) scale(0.8);
            }
            100% {
              opacity: 1;
              transform: rotate(0deg) scale(1);
            }
          }

          @keyframes zoomIn {
            0% {
              opacity: 0;
              transform: scale(0.5);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes slideRightIn {
            0% {
              opacity: 0;
              transform: translateX(40px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes flipIn {
            0% {
              opacity: 0;
              transform: rotateY(-90deg) scale(0.8);
            }
            100% {
              opacity: 1;
              transform: rotateY(0deg) scale(1);
            }
          }

          @keyframes glowIn {
            0% {
              opacity: 0;
              transform: scale(0.9);
              filter: brightness(0.5);
            }
            50% {
              opacity: 0.8;
              filter: brightness(1.2);
            }
            100% {
              opacity: 1;
              transform: scale(1);
              filter: brightness(1);
            }
          }

          /* Section animations */
          @keyframes slideUpSection {
            0% {
              opacity: 0;
              transform: translateY(30px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeInSection {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }

          @keyframes scaleInSection {
            0% {
              opacity: 0;
              transform: scale(0.95);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes slideLeftSection {
            0% {
              opacity: 0;
              transform: translateX(-20px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes bounceInSection {
            0% {
              opacity: 0;
              transform: translateY(-20px);
            }
            50% {
              opacity: 0.8;
              transform: translateY(5px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes rotateInSection {
            0% {
              opacity: 0;
              transform: rotate(-2deg);
            }
            100% {
              opacity: 1;
              transform: rotate(0deg);
            }
          }

          @keyframes zoomInSection {
            0% {
              opacity: 0;
              transform: scale(0.9);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes slideRightSection {
            0% {
              opacity: 0;
              transform: translateX(20px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes flipInSection {
            0% {
              opacity: 0;
              transform: rotateY(-5deg);
            }
            100% {
              opacity: 1;
              transform: rotateY(0deg);
            }
          }

          @keyframes glowInSection {
            0% {
              opacity: 0;
              filter: brightness(0.8);
            }
            100% {
              opacity: 1;
              filter: brightness(1);
            }
          }

          /* Mobile Responsive */
          @media (max-width: 991px) {
            .cool-random-image-section {
              margin: 6px 0;
              padding: 3px 0;
            }
            
            .cool-image-container {
              height: 300px;
            }
            
            .cool-image-title {
              font-size: 2rem;
            }
            
            .cool-image-description {
              font-size: 1rem;
            }
          }

          @media (max-width: 575px) {
            .cool-random-image-section {
              margin: 4px 0;
              padding: 2px 0;
            }
            
            .cool-image-container {
              height: 250px;
            }
            
            .cool-image-title {
              font-size: 1.5rem;
            }
            
            .cool-image-description {
              font-size: 0.9rem;
            }
            
            .cool-image-btn {
              padding: 12px 24px;
              font-size: 0.9rem;
            }
          }
          
          /* Hide BannerCollectionSlider on mobile devices */
          @media (max-width: 767px) {
            .banner-collection-slider-wrapper {
              display: none !important;
            }
          }

          /* Make offer slider titles always bluish (rectangular, square, circular) */
          .collection-item-v3 .collection-content .title,
          .collection-item-v3 .collection-content .title:hover {
            color: #0e5aa7 !important;
          }

          .collection-item-circle .collection-content .title,
          .collection-item-circle .collection-content .title:hover,
          .collection-item-circle .collection-content .link.title {
            color: #0e5aa7 !important;
          }

          /* Loading animation for skeleton loaders */
          @keyframes pulse {
            0%, 100% { 
              opacity: 1;
              transform: scale(1);
            }
            50% { 
              opacity: 0.5;
              transform: scale(0.98);
            }
          }

          /* Responsive loading indicators */
          @media (max-width: 767px) {
            .offers-loading-container {
              padding: 20px 10px !important;
            }
            .offers-loading-item {
              min-width: 150px !important;
              height: 200px !important;
            }
          }

          /* Banner loader - make sure it's visible on all screens */
          .banner-loader-container {
            z-index: 10;
            overflow: visible !important;
          }

          .banner-loader-container .preload-logo {
            position: relative;
            width: 60px;
            height: 60px;
            margin: auto;
          }

          @media (max-width: 575px) {
            .banner-loader-container {
              min-height: 200px !important;
              height: 25vh !important;
            }
          }

          @media (max-width: 767px) {
            .banner-loader-container {
              min-height: 220px !important;
            }
          }
        `}
      </style>
      {/* RTL */}
      {/* <a href="javascript:void(0);" id="toggle-rtl" className="tf-btn animate-hover-btn btn-fill">RTL</a> */}
      {/* /RTL  */}

      {/* preload - only for categories */}
      {isLoading && (
        <div className="preload preload-container">
          <div className="preload-logo">
            <div className="spinner"></div>
          </div>
        </div>
      )}

      <div id="wrapper">
        {/* announcement-bar */}
        {/* <NewsTicker
          items={[
            "Abdelaziz El Sallab: Premium Tiles & Quality Ceramics since 1912",
            "Discover luxury bathroom collections & elegant home decorations",
            "Premium porcelain tiles for every space — indoor & outdoor",
            "Modern sanitary ware: durability, design, and trusted brands",
            "Exclusive seasonal offers on tiles, mixers, and accessories",
            "Transform your home with curated décor & stylish finishes",
            "Expert guidance in-showroom and online — plan your renovation",
            "Fast delivery nationwide — quality you can trust",
          ]}
        /> */}
        <Header
          onProfileClick={() => {
            setAuthView("login");
            setIsAuthOpen(true);
          }}
        />
        {/* Desktop categories slider - hidden on mobile */}
        <div className="desktop-categories-wrapper">
          <CircularCategoriesSlider
            categories={(categories || []).map((c) => ({
              ...c,
              link: c.id
                ? `/shop/collection?category_id=${c.id}`
                : c.link || "/shop/collection",
            }))}
            shopAllLink="/shop/collection?category_id=1563"
          />
        </div>

        {/* BannerCollectionSlider - loads independently from offers API */}
        <div className="banner-collection-slider-wrapper">
          {isLoadingBannerOffers ? (
            <div
              className="preload preload-container w-100 text-center py-5"
              style={{
                height: "30vh",
                minHeight: "250px",
                maxHeight: "400px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
                width: "100%",
                position: "relative",
              }}
            >
              <div className="preload-logo">
                <div className="spinner"></div>
              </div>
            </div>
          ) : bannerOffersData && bannerOffersData.length > 0 ? (
            <BannerCollectionSlider
              bannerOffers={bannerOffersData}
              title=""
              subtitle=""
              buttonText=""
              buttonLink=""
            />
          ) : null}
        </div>

        {/* slider */}
        {(isMobile ? isLoadingOffers : isLoadingBannerOffers) ? (
          // Show loading indicator: on mobile wait for /offers API, on desktop wait for banner-offers API
          <div
            className="banner-loader-container"
            style={{
              height: "30vh",
              minHeight: "250px",
              maxHeight: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              width: "100%",
              position: "relative",
            }}
          >
            <div className="preload-logo">
              <div className="spinner"></div>
            </div>
          </div>
        ) : (
          (() => {
            const dynamicSlides = (bannerOffers || []).map((o) => ({
              image: o.imageUrl,
              alt: o.name,
              title: "",
              description: o.promotionalText || "",
              buttonText:
                language === "ar" ? "تسوق المجموعة" : "Shop collection",
              buttonLink:
                o.id && o.name
                  ? `/shop/default?offer=${encodeURIComponent(createSlug(o.name))}&offer_id=${o.id}`
                  : o.id
                    ? `/shop/default?offer_id=${o.id}`
                    : "/shop-default",
            }));
            const fallbackSlides = [
              {
                image: "/assets/images/slider/667799.png",
                alt: "667799",
                // title: "Elegance",
                // description: "From casual to formal, we've got you covered",
                buttonText:
                  language === "ar" ? "تسوق المجموعة" : "Shop collection",
                buttonLink: "/shop/collection",
              },
              {
                image: "/assets/images/slider/667789.png",
                alt: "667789",
                // title: "Elegance",
                // description: "From casual to formal, we've got you covered",
                buttonText:
                  language === "ar" ? "تسوق المجموعة" : "Shop collection",
                buttonLink: "/shop/collection",
              },
              {
                image: "/assets/images/slider/667788.png",
                alt: "667788",
                // title: "Elegance",
                // description: "From casual to formal, we've got you covered",
                buttonText:
                  language === "ar" ? "تسوق المجموعة" : "Shop collection",
                buttonLink: "/shop/collection",
              },
              // {
              //   image: "/assets/images/slider/banner1.png",
              //   alt: "banner1",
              //   // title: "Elegance",
              //   // description: "From casual to formal, we've got you covered",
              //   buttonText: "Shop collection",
              //   buttonLink: "/shop/collection",
              // },
              // {
              //   image: "/assets/images/slider/banner2.png",
              //   alt: "women-slideshow-01",
              //   // title: "Elegance",
              //   // description: "From casual to formal, we've got you covered",
              //   buttonText: "Shop collection",
              //   buttonLink: "/shop/collection",
              // },
              // {
              //   image: "/assets/images/slider/download.webp",
              //   alt: "women-slideshow-01",
              //   // title: "Elegance",
              //   // description: "From casual to formal, we've got you covered",
              //   buttonText: "Shop collection",
              //   buttonLink: "/shop/collection",
              // },
              // {
              //   image: "/assets/images/slider/download1.webp",
              //   alt: "women-slideshow-02",
              //   // title: "Boutique",
              //   // description: "From casual to formal, we've got you covered",
              //   buttonText: "Shop collection",
              //   buttonLink: "/shop/collection",
              // },
              // {
              //   image: "/assets/images/slider/download2.webp",
              //   alt: "women-slideshow-03",
              //   // title: "Luxury",
              //   // description: "From casual to formal, we've got you covered",
              //   buttonText: "Shop collection",
              //   buttonLink: "/shop/collection",
              // },
            ];
            const slides =
              dynamicSlides.length > 0 && !isErrorOffers
                ? dynamicSlides
                : fallbackSlides;
            return (
              <>
                {/* Desktop carousel slider - hidden on mobile */}
                {/* <div className="desktop-carousel-wrapper">
                <CarouselSlider slides={[...fallbackSlides]} />
              </div> */}
                {/* Mobile slides slider - hidden on desktop, only show dynamic slides */}
                {dynamicSlides.length > 0 && (
                  <MobileSlidesSlider slides={dynamicSlides} />
                )}
              </>
            );
          })()
        )}

        {/* Mobile categories grid - hidden on desktop, appears below slider */}
        <MobileCategoriesGrid
          categories={(categories || []).map((c) => ({
            ...c,
            link: c.id
              ? `/shop/collection?category_id=${c.id}`
              : c.link || "/shop/collection",
          }))}
          shopAllLink="/shop/collection?category_id=1563"
        />

        {/* Spotlight Sections (from offers, grouped by promotional text) */}
        {isLoadingOffers ? (
          // Show loading indicators for offer sliders
          <div style={{ padding: "40px 0" }}>
            {[1, 2, 3].map((i) => (
              <div
                key={`loader-${i}`}
                style={{
                  margin: "20px auto",
                  maxWidth: "1200px",
                  padding: "0 20px",
                }}
              >
                <div
                  style={{
                    height: "40px",
                    width: "200px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                ></div>
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                    overflowX: "hidden",
                  }}
                >
                  {[1, 2, 3, 4].map((j) => (
                    <div
                      key={`item-${j}`}
                      style={{
                        minWidth: "250px",
                        height: "300px",
                        backgroundColor: "#e0e0e0",
                        borderRadius: "12px",
                        animation: "pulse 1.5s ease-in-out infinite",
                        animationDelay: `${j * 0.1}s`,
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            ))}
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}</style>
          </div>
        ) : (
          (() => {
            if (
              !spotlightOffersGrouped ||
              Object.keys(spotlightOffersGrouped).length === 0
            ) {
              return null;
            }

            const mapProducts = (offer) => {
              let prods = Array.isArray(offer.products) ? offer.products : [];
              if (
                (!prods || prods.length === 0) &&
                Array.isArray(offer.relatedOffers) &&
                offer.relatedOffers.length > 0
              ) {
                prods = offer.relatedOffers.flatMap((rel) =>
                  Array.isArray(rel.products) ? rel.products : [],
                );
              }
              let mapped = prods.map((p) => ({
                name: p.name || "",
                image: p.image_url || p.imageUrl || "",
                link: p.id ? `/product-detail?id=${p.id}` : "/product-detail",
              }));
              if (!mapped || mapped.length === 0) {
                if (offer.imageUrl) {
                  mapped = [
                    {
                      name: offer.name || "",
                      image: offer.imageUrl,
                      link: "/shop-collection-sub",
                    },
                  ];
                }
              }
              return mapped;
            };

            // List of shape components with their props
            const shapeComponents = [
              {
                component: MasonryLayoutV4,
                props: {
                  items: [
                    {
                      id: 711,
                      image: "/assets/images/img/2.jpg",
                      alt: "collection-img",
                      link: "/shop/collection",
                    },
                    {
                      id: 712,
                      image: "/assets/images/img/100.jpg",
                      alt: "collection-img",
                      link: "/shop/collection",
                    },
                    {
                      id: 713,
                      image: "/assets/images/img/300.jpg",
                      alt: "collection-img",
                      link: "/shop/collection",
                    },
                  ],
                },
              },
              {
                component: BannerCollection,
                props: {
                  // image: "/assets/images/img/5.jpg",
                  image: "/assets/images/img/500.jpeg",
                  title: "",
                  subtitle: "",
                  buttonText: language === "ar" ? "تسوق الآن" : "Shop Now",
                  buttonLink: "/shop/collection",
                },
              },
              {
                component: TextImageLayout,
                props: {
                  images: [
                    {
                      image: "/assets/images/img/800.jpg",
                      offer_id: "700",
                      name:
                        offers?.find((o) => String(o.id) === "700")?.name ||
                        null,
                    },
                    {
                      image: "/assets/images/img/600.jpg",
                      offer_id: "701",
                      name:
                        offers?.find((o) => String(o.id) === "701")?.name ||
                        null,
                    },
                    {
                      image: "/assets/images/img/700.jpg",
                      offer_id: "698",
                      name:
                        offers?.find((o) => String(o.id) === "698")?.name ||
                        null,
                    },
                  ],
                  subtitle:
                    language === "ar"
                      ? "هدية مثالية لك"
                      : "PERFECT GIFT FOR YOU",
                  title:
                    language === "ar"
                      ? "أفضل الاختيارات من عبدالعزيز السلاب"
                      : "Top Picks from Abdelaziz El Sallab",
                  description:
                    language === "ar"
                      ? "يتم التوصيل كل شهر! مثالي لمجموعتك المفضلة أو هدايا للأحباء."
                      : "Delivered every month! Perfect for your favorite collection or gifts for loved ones.",
                  buttonText:
                    language === "ar" ? "تسوق المجموعة" : "Shop collection",
                  buttonLink: getOfferLink(700),
                },
              },
              {
                component: Lookbook,
                props: {
                  leftImage: "/assets/images/img/2.jpg",
                  label:
                    language === "ar"
                      ? "تخفيضات الربيع 30% خصم"
                      : "SPRING SALE 30% OFF",
                  title:
                    language === "ar"
                      ? "تسوق هذه المجموعة"
                      : "Shop this collection",
                  products: [
                    {
                      id: 1,
                      name:
                        language === "ar"
                          ? "بلاط بورسلين فاخر"
                          : "Luxury Porcelain Tiles",
                      image: "/assets/images/img/7.jpg",
                      hoverImage: "/assets/images/img/7.jpg",
                      price: language === "ar" ? "عرض ساخن" : "Hot Deal",
                    },
                    {
                      id: 2,
                      name:
                        language === "ar"
                          ? "بلاط بورسلين فاخر"
                          : "Luxury Porcelain Tiles",
                      image: "/assets/images/img/7.jpg",
                      hoverImage: "/assets/images/img/7.jpg",
                      price: language === "ar" ? "عرض ساخن" : "Hot Deal",
                    },
                  ],
                },
              },
              {
                component: BannerHeroCollection,
                props: {
                  backgroundImage: "/assets/images/img/1.jpg",
                  title:
                    language === "ar" ? "تسوق فئاتنا" : "Shop our categories",
                  description:
                    language === "ar"
                      ? "اكتشف مجموعتنا الواسعة من المنتجات في فئات مختلفة"
                      : "Explore our wide range of products in different categories",
                  buttonText: language === "ar" ? "تسوق الآن" : "Shop Now",
                  buttonLink: "/shop/collection",
                },
              },
              {
                component: MasonryLayoutStyle2,
                props: {
                  items: [
                    {
                      id: 714,
                      image: "/assets/images/img/3.jpg",
                      title: language === "ar" ? "تسوق الكل" : "Shop All",
                      link: "/shop/collection",
                    },
                    {
                      id: 715,
                      image: "/assets/images/img/8.jpg",
                      title:
                        language === "ar" ? "تسوق المجموعة" : "Shop Collection",
                      link: "/shop/collection",
                    },
                    {
                      id: 716,
                      image: "/assets/images/img/7.jpg",
                      title:
                        language === "ar" ? "تسوق المجموعة" : "Shop Collection",
                      link: "/shop/collection",
                    },
                    {
                      id: 717,
                      image: "/assets/images/img/2.jpg",
                      title:
                        language === "ar" ? "تسوق المجموعة" : "Shop Collection",
                      link: "/shop/collection",
                    },
                  ],
                },
              },
              {
                component: BrandCarousel,
                props: {
                  brands: [
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/515514",
                      name: "KOHLER",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/515626",
                      name: "جروهه",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/539106",
                      name: "Alsa",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/515755",
                      name: "KLUDI RAK",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/515776",
                      name: "SARR DESIGN",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/515795",
                      name: "جود",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/516332",
                      name: "ROCA",
                    },
                    {
                      image: "https://erp-elsalab.odoo.com/web/image/539107",
                      name: "Elsallab",
                    },
                  ],
                },
              },
              {
                component: BannerWithProducts,
                props: {
                  banner: {
                    id: 718,
                    image: "/assets/images/img/2.jpg",
                    title:
                      language === "ar" ? "مجموعة الموسم" : "SEASON COLLECTION",
                    subtitle:
                      language === "ar"
                        ? "كل قطعة مصنوعة لتدوم بعد الموسم"
                        : "Every piece is made to last beyond the season",
                    buttonText:
                      language === "ar" ? "تسوق المجموعة" : "SHOP COLLECTION",
                    link: getOfferLink(718),
                  },
                  products: [
                    {
                      id: 703,
                      name:
                        language === "ar"
                          ? "تسوق مجموعتنا"
                          : "Shop Our Collection",
                      image: "/assets/images/img/8000.jpg",
                      hoverImage: "/assets/images/img/8000.jpg",
                      price: language === "ar" ? "عرض ساخن" : "Hot Deal",
                      discount: "-1%",
                    },
                    {
                      id: 702,
                      name:
                        language === "ar"
                          ? "تسوق مجموعتنا"
                          : "Shop Our Collection",
                      image: "/assets/images/img/7000.jpg",
                      hoverImage: "/assets/images/img/7000.jpg",
                      price: language === "ar" ? "عرض ساخن" : "Hot Deal",
                    },
                    {
                      id: 702,
                      name:
                        language === "ar"
                          ? "تسوق مجموعتنا"
                          : "Shop Our Collection",
                      image: "/assets/images/img/7000.jpg",
                      hoverImage: "/assets/images/img/7000.jpg",
                      price: language === "ar" ? "عرض خاص" : "Special Offer",
                    },
                    {
                      id: 703,
                      name:
                        language === "ar"
                          ? "تسوق مجموعتنا"
                          : "Shop Our Collection",
                      image: "/assets/images/img/8000.jpg",
                      hoverImage: "/assets/images/img/8000.jpg",
                      price: language === "ar" ? "عرض خاص" : "Special Offer",
                    },
                  ],
                },
              },
              {
                component: MasonryLayoutV2,
                props: {
                  title: language === "ar" ? "مجموعاتنا" : "Our Collections",
                  subtitle:
                    language === "ar"
                      ? "حيث تتحقق قوائم الأمنيات. اكتشف قطع أحلامهم (أو أحلامك)."
                      : "Where wishlists come true. Discover the pieces of their (or your) dreams.",
                  items: [
                    {
                      id: 719,
                      image: "/assets/images/img/6.jpg",
                      title:
                        language === "ar"
                          ? "المجموعات الكلاسيكية"
                          : "Classic Collections",
                      link: "/shop/collection",
                    },
                    {
                      id: 720,
                      image: "/assets/images/img/7.jpg",
                      title:
                        language === "ar"
                          ? "إكسسوارات 5000+ جنيه"
                          : "Accessories EGP 5000+",
                      link: "/shop/collection",
                    },
                    {
                      id: 721,
                      image: "/assets/images/img/8.jpg",
                      title:
                        language === "ar" ? "الأكثر مبيعاً" : "Best Sellers",
                      link: "/shop/collection",
                    },
                    {
                      id: 722,
                      image: "/assets/images/img/7.jpg",
                      title:
                        language === "ar"
                          ? "هدايا أقل من 5000 جنيه"
                          : "Gifts Under EGP 5000",
                      link: "/shop/collection",
                    },
                    {
                      id: 723,
                      image: "/assets/images/img/8.jpg",
                      title:
                        language === "ar"
                          ? "هدايا أكثر من 9000 جنيه"
                          : "Gifts Over EGP 9000",
                      link: "/shop/collection",
                    },
                    {
                      id: 724,
                      image: "/assets/images/img/6.jpg",
                      title: language === "ar" ? "داخلي" : "Interior",
                      link: "/shop/collection",
                    },
                  ],
                },
              },
            ];

            // Function to render a shape component in order (cycles through all shapes before repeating)
            const renderRandomShape = (index) => {
              // Calculate shape index based on position: shapes appear every 3 items (at indices 2, 5, 8, etc.)
              // Use modulo to cycle through all shapes before repeating
              const shapeIndex = Math.floor(index / 3) % shapeComponents.length;
              const selectedShape = shapeComponents[shapeIndex];
              const ShapeComponent = selectedShape.component;
              const isBannerCollection = ShapeComponent === BannerCollection;

              return (
                <div
                  key={`random-shape-${index}`}
                  className={
                    isBannerCollection ? "banner-collection-wrapper" : ""
                  }
                >
                  <ShapeComponent {...selectedShape.props} />
                </div>
              );
            };

            const offerEntries = Object.entries(spotlightOffersGrouped);

            return (
              <div>
                {offerEntries.map(([promoText, offers], index) => {
                  // Build one slider per group like mobile, using offers as items
                  const groupItems = offers.map((offer) => ({
                    name: offer.name || "",
                    image: offer.imageUrl || "",
                    link:
                      offer.id && offer.name
                        ? `/shop/default?offer=${encodeURIComponent(createSlug(offer.name))}&offer_id=${offer.id}`
                        : offer.id
                          ? `/shop/default?offer_id=${offer.id}`
                          : "/shop-default",
                    backgroundColor: offer.backgroundColor || null,
                  }));

                  const first = offers[0] || {};
                  const anyBg = offers.some(
                    (o) => !!o.backgroundColor && o.backgroundColor !== "none",
                  );
                  const shape = ((first.shape || "") + "").toLowerCase();

                  // Collect all products from offers and related_offers
                  const allProducts = offers.flatMap((offer) => {
                    const directProducts = Array.isArray(offer.products)
                      ? offer.products
                      : [];
                    const relatedProducts = Array.isArray(offer.relatedOffers)
                      ? offer.relatedOffers.flatMap((rel) =>
                          Array.isArray(rel.products) ? rel.products : [],
                        )
                      : [];

                    return [...directProducts, ...relatedProducts].map((p) => ({
                      id: String(p.id || p.template_id || ""),
                      title: p.name || "",
                      name: p.name || "",
                      price: typeof p.price === "number" ? p.price : null,
                      list_price:
                        typeof p.list_price === "number" ? p.list_price : null,
                      availability:
                        p.stock && p.stock[0] && p.stock[0].total_free > 0
                          ? "In stock"
                          : "Out of stock",
                      brand: p.att_model_name || "",
                      colors: p.att_color ? [p.att_color] : [],
                      sizes:
                        p.att_size && p.att_size !== "-" ? [p.att_size] : [],
                      att_size:
                        p.att_size && p.att_size !== "-" ? p.att_size : null,
                      att_specifications: p.att_specifications || null,
                      description: p.description || null,
                      categ_name: p.categ_name || null,
                      is_favorite: p.is_favorite || false,
                      current_offer: p.current_offer || null,
                      att_color: p.att_color || null,
                      image:
                        p.image_url_1920 ||
                        p.image_url ||
                        (p.id
                          ? `https://erp-elsalab.odoo.com/web/image/${p.id}`
                          : ""),
                      image_url_1920: p.image_url_1920 || null,
                      image_url: p.image_url || null,
                      stock: p.stock || [],
                      package_quantity: p.package_quantity || 1,
                    }));
                  });

                  const sliderComponent = (
                    <div
                      className="home-offer-block"
                      key={`offer-group-${promoText}`}
                    >
                      {shape === "circle" ? (
                        <CircularOffersSlider
                          title={promoText}
                          products={groupItems}
                          backgroundColor={first.backgroundColor}
                        />
                      ) : shape === "square" || shape === "big_square" ? (
                        <SquareOffersSlider
                          title={promoText}
                          products={groupItems}
                        />
                      ) : shape === "rectangle" ? (
                        // For rectangle shape, always use RectangularOffersSlider
                        <RectangularOffersSlider
                          title={promoText}
                          products={groupItems}
                        />
                      ) : anyBg ? (
                        // For other shapes with background color, use SquareOffersSlider2
                        <SquareOffersSlider2
                          title={promoText}
                          products={groupItems}
                          backgroundColor={first.backgroundColor}
                        />
                      ) : (
                        // Default fallback for other shapes
                        <RectangularOffersSlider
                          title={promoText}
                          products={groupItems}
                        />
                      )}
                    </div>
                  );

                  // Get the name from the first item in relatedOffers
                  const firstRelatedOfferName = (() => {
                    for (const offer of offers) {
                      if (
                        Array.isArray(offer.relatedOffers) &&
                        offer.relatedOffers.length > 0 &&
                        offer.relatedOffers[0]?.name
                      ) {
                        return offer.relatedOffers[0].name;
                      }
                    }
                    return null;
                  })();

                  // Products section if products are available
                  const productsSection =
                    allProducts.length > 0 ? (
                      <div className="products-slider-section">
                        <ProductsSlider
                          key={`products-section-${promoText}`}
                          title={
                            firstRelatedOfferName ||
                            (language === "ar" ? "عروض خاصة" : "Special Offers")
                          }
                          products={allProducts}
                          showTitle={true}
                        />
                      </div>
                    ) : null;

                  // Show random shape after every 3 sliders (at index 2, 5, 8, etc.)
                  if (
                    (index + 1) % 3 === 0 &&
                    index < offerEntries.length - 1
                  ) {
                    return (
                      <React.Fragment key={`fragment-${index}`}>
                        {sliderComponent}
                        {productsSection}
                        {renderRandomShape(index)}
                      </React.Fragment>
                    );
                  }

                  return (
                    <React.Fragment key={`fragment-${index}`}>
                      {sliderComponent}
                      {productsSection}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()
        )}
        {/* Categories */}
        <SquareCategoriesSlider
          title={
            language === "ar" ? "فئات قد تعجبك" : "Categories you might like"
          }
          categories={(categories || []).map((c) => ({
            ...c,
            link: c.id
              ? `/shop/default?category_id=${c.id}`
              : c.link || "/shop/collection",
          }))}
        />

        {/* Filter rectangle offers for OffersTabs */}
        {isLoadingOffers ? (
          // Show loading indicator for OffersTabs
          <div
            style={{
              margin: "40px auto",
              maxWidth: "1200px",
              padding: "0 20px",
            }}
          >
            <div
              style={{
                height: "50px",
                width: "300px",
                backgroundColor: "#e0e0e0",
                borderRadius: "8px",
                marginBottom: "30px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            ></div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: "20px",
              }}
            >
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={`tab-loader-${i}`}
                  style={{
                    height: "350px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "12px",
                    animation: "pulse 1.5s ease-in-out infinite",
                    animationDelay: `${i * 0.1}s`,
                  }}
                ></div>
              ))}
            </div>
          </div>
        ) : (
          (() => {
            // Get all offers from spotlightOffersGrouped
            const allOffers = [];
            if (spotlightOffersGrouped) {
              Object.values(spotlightOffersGrouped).forEach((offers) => {
                offers.forEach((offer) => {
                  const shape = ((offer.shape || "") + "").toLowerCase();
                  if (shape === "rectangle") {
                    // Map offer to product format
                    const products = Array.isArray(offer.products)
                      ? offer.products
                      : [];
                    if (products.length > 0) {
                      products.forEach((product) => {
                        allOffers.push({
                          name: product.name || offer.name || "",
                          image:
                            product.image_url ||
                            product.imageUrl ||
                            offer.imageUrl ||
                            "",
                          link:
                            offer.id && offer.name
                              ? `/shop/default?offer=${encodeURIComponent(createSlug(offer.name))}&offer_id=${offer.id}`
                              : offer.id
                                ? `/shop/default?offer_id=${offer.id}`
                                : "/shop/default",
                          category: offer.name || "Product",
                        });
                      });
                    } else if (offer.imageUrl) {
                      allOffers.push({
                        name: offer.name || "",
                        image: offer.imageUrl,
                        link: offer.id
                          ? `/shop/default?offer_id=${offer.id}`
                          : "/shop/default",
                        category: offer.name || "Product",
                      });
                    }
                  }
                });
              });
            }

            // Split rectangle offers in half: first half for best seller, second half for on sale
            const totalOffers = allOffers.length;
            const midpoint = Math.ceil(totalOffers / 2);
            const bestSellers = allOffers.slice(0, midpoint);
            const onSale = allOffers.slice(midpoint);

            return <OffersTabs bestSellers={bestSellers} onSale={onSale} />;
          })()
        )}

        <FeaturesIconsSlider
          categories={[
            {
              icon: "icon-shipping",
              title: language === "ar" ? "شحن سريع" : "Fast Shipping",
              description:
                language === "ar"
                  ? "شحن سريع إلى باب منزلك"
                  : "Fast shipping to your door",
            },
            {
              icon: "icon-payment fs-22",
              title: language === "ar" ? "دفع مرن" : "Flexible Payment",
              description:
                language === "ar"
                  ? "ادفع ببطاقات ائتمانية متعددة"
                  : "Pay with Multiple Credit Cards",
            },
            {
              icon: "icon-return fs-22",
              title: language === "ar" ? "إرجاع خلال 14 يوم" : "14 Day Returns",
              description:
                language === "ar"
                  ? "خلال 30 يوم للاستبدال"
                  : "Within 30 days for an exchange",
            },
            {
              icon: "icon-suport",
              title: language === "ar" ? "دعم ممتاز" : "Premium Support",
              description:
                language === "ar"
                  ? "دعم ممتاز متميز"
                  : "Outstanding premium support",
            },
          ]}
        />
        <Footer />
        <GoTo />
        <AuthModal
          isOpen={isAuthOpen}
          view={authView}
          onClose={() => setIsAuthOpen(false)}
          onChangeView={(v) => setAuthView(v)}
        />
      </div>
    </div>
  );
}

export default HomePage;
